import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Card, PageHeader, Input, Select, Badge } from "@/components/ui-kit";
import { formatDateTime, formatIDR, formatNumber } from "@/lib/format";
import { usePagination } from "@/hooks/usePagination";
import { DatePicker } from "@/components/DatePicker";
import { History, Search, TrendingUp, TrendingDown, Minus, User, Calendar, Package } from "lucide-react";

/**
 * Iter v34co (#6) — Halaman Riwayat Perubahan Harga.
 * Menampilkan tabel semua perubahan harga (old → new, diff, %, direction)
 * dari 2 sumber gabungan: `db.alerts` (recent enriched) + `db.system_logs` (backfill).
 */
const FIELD_LABELS = {
  price_non_vat: "Harga Non-PPN",
  price_ex_vat: "Harga Excl. PPN",
  price_with_vat: "Harga Incl. PPN",
  delivery_cost: "Ongkos Kirim",
  unloading_fee: "Biaya Bongkar",
  material_name: "Nama Material",
  unit: "Satuan",
  delivery_location: "Lokasi",
  client_name: "Klien",
  category: "Kategori",
  description: "Deskripsi",
};

const isPriceField = (k) => ["price_non_vat", "price_ex_vat", "price_with_vat", "delivery_cost", "unloading_fee"].includes(k);

function DirectionIcon({ dir }) {
  if (dir === "naik") return <TrendingUp className="h-3.5 w-3.5 text-red-600" />;
  if (dir === "turun") return <TrendingDown className="h-3.5 w-3.5 text-emerald-600" />;
  return <Minus className="h-3.5 w-3.5 text-stone-400" />;
}

function ChangeCell({ field, ch }) {
  const label = FIELD_LABELS[field] || field;
  const isNumeric = isPriceField(field) && ch.diff !== null && ch.diff !== undefined;
  const fmt = isNumeric ? (v) => formatIDR(v) : (v) => (v === null || v === undefined ? "—" : String(v));
  const pct = typeof ch.pct === "number" ? ch.pct : null;
  return (
    <div className="rounded-lg border border-stone-200 bg-white p-2.5 space-y-1">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold">{label}</span>
        <DirectionIcon dir={ch.direction} />
      </div>
      <div className="grid grid-cols-2 gap-1 text-xs">
        <div className="text-stone-500">
          <span className="text-[9px] uppercase tracking-wider block">Lama</span>
          <span className="font-mono-num text-stone-700 line-through decoration-red-300">{fmt(ch.old)}</span>
        </div>
        <div>
          <span className="text-[9px] uppercase tracking-wider text-stone-500 block">Baru</span>
          <span className="font-mono-num text-stone-900 font-semibold">{fmt(ch.new)}</span>
        </div>
      </div>
      {isNumeric && (
        <div className={`text-[10px] font-mono-num ${ch.direction === "naik" ? "text-red-700" : ch.direction === "turun" ? "text-emerald-700" : "text-stone-500"}`}>
          {ch.diff > 0 ? "+" : ""}{formatIDR(ch.diff)}
          {pct !== null && <span className="ml-1">({pct > 0 ? "+" : ""}{formatNumber(pct, 2)}%)</span>}
        </div>
      )}
    </div>
  );
}

export default function PriceHistory() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [dirFilter, setDirFilter] = useState(""); // all/naik/turun/tetap
  const [actorFilter, setActorFilter] = useState("");

  const load = () => {
    setLoading(true);
    const params = {};
    if (dateFrom) params.start_date = dateFrom;
    if (dateTo) params.end_date = dateTo;
    api.get("/prices/history", { params })
      .then((r) => setRows(r.data || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [dateFrom, dateTo]); // eslint-disable-line

  // Aggregate direction per row (based on price fields)
  const enriched = useMemo(() => rows.map((r) => {
    const dirs = Object.entries(r.changes || {})
      .filter(([k]) => isPriceField(k))
      .map(([, v]) => v.direction);
    let overall = "tetap";
    if (dirs.some((d) => d === "naik") && !dirs.some((d) => d === "turun")) overall = "naik";
    else if (dirs.some((d) => d === "turun") && !dirs.some((d) => d === "naik")) overall = "turun";
    else if (dirs.some((d) => d === "naik") && dirs.some((d) => d === "turun")) overall = "campuran";
    return { ...r, _overall: overall };
  }), [rows]);

  const filtered = useMemo(() => {
    const qs = q.trim().toLowerCase();
    return enriched.filter((r) => {
      if (dirFilter && r._overall !== dirFilter) return false;
      if (actorFilter && !(r.actor_name || "").toLowerCase().includes(actorFilter.toLowerCase())) return false;
      if (!qs) return true;
      return (
        (r.material_name || "").toLowerCase().includes(qs) ||
        (r.location || "").toLowerCase().includes(qs) ||
        (r.client_name || "").toLowerCase().includes(qs) ||
        (r.actor_name || "").toLowerCase().includes(qs)
      );
    });
  }, [enriched, q, dirFilter, actorFilter]);

  const actors = useMemo(() =>
    Array.from(new Set(enriched.map((r) => r.actor_name).filter(Boolean))).sort(),
  [enriched]);

  const summary = useMemo(() => ({
    total: enriched.length,
    naik: enriched.filter((r) => r._overall === "naik").length,
    turun: enriched.filter((r) => r._overall === "turun").length,
    campuran: enriched.filter((r) => r._overall === "campuran").length,
  }), [enriched]);

  const { pageItems, Pagination } = usePagination(filtered, { perPage: 25 });

  return (
    <div className="space-y-6" data-testid="price-history-page">
      <PageHeader
        breadcrumb="Operasional · Riwayat"
        title="Riwayat Perubahan Harga"
        description="Log lengkap setiap perubahan harga, ongkos kirim, biaya bongkar & metadata katalog. Sumber: alerts + system logs."
        icon={History}
      />

      {/* Summary chips */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="p-4" data-testid="ph-total">
          <div className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">Total Event</div>
          <div className="font-display text-3xl mt-1 text-stone-900 font-mono-num">{summary.total}</div>
        </Card>
        <Card className="p-4 !border-red-100 bg-red-50/40" data-testid="ph-naik">
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-red-700 font-semibold">
            <TrendingUp className="h-3 w-3" /> Naik
          </div>
          <div className="font-display text-3xl mt-1 text-red-800 font-mono-num">{summary.naik}</div>
        </Card>
        <Card className="p-4 !border-emerald-100 bg-emerald-50/40" data-testid="ph-turun">
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-emerald-700 font-semibold">
            <TrendingDown className="h-3 w-3" /> Turun
          </div>
          <div className="font-display text-3xl mt-1 text-emerald-800 font-mono-num">{summary.turun}</div>
        </Card>
        <Card className="p-4" data-testid="ph-campuran">
          <div className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">Campuran / Lainnya</div>
          <div className="font-display text-3xl mt-1 text-stone-900 font-mono-num">{summary.campuran}</div>
        </Card>
      </div>

      {/* Filters */}
      <Card className="p-4">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <div className="md:col-span-2 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-stone-400" />
            <Input
              className="!pl-9"
              placeholder="Cari material, lokasi, klien, actor…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="ph-search"
            />
          </div>
          <DatePicker value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} placeholder="Dari tanggal" data-testid="ph-date-from" />
          <DatePicker value={dateTo} onChange={(e) => setDateTo(e.target.value)} placeholder="Sampai tanggal" data-testid="ph-date-to" />
          <Select value={dirFilter} onChange={(e) => setDirFilter(e.target.value)} data-testid="ph-dir-filter">
            <option value="">Semua Arah</option>
            <option value="naik">Hanya Naik</option>
            <option value="turun">Hanya Turun</option>
            <option value="campuran">Campuran</option>
            <option value="tetap">Tetap (non-harga)</option>
          </Select>
          <Select value={actorFilter} onChange={(e) => setActorFilter(e.target.value)} data-testid="ph-actor-filter" className="md:col-span-2">
            <option value="">Semua Actor</option>
            {actors.map((a) => <option key={a} value={a}>{a}</option>)}
          </Select>
          <button
            onClick={() => { setQ(""); setDateFrom(""); setDateTo(""); setDirFilter(""); setActorFilter(""); }}
            className="text-xs text-stone-500 hover:text-stone-900 underline"
            data-testid="ph-reset-filters"
          >Reset Filter</button>
        </div>
      </Card>

      {/* Table */}
      <Card className="overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-stone-400 text-sm">Memuat riwayat…</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center text-stone-400 text-sm" data-testid="ph-empty">
            Belum ada riwayat perubahan harga sesuai filter.
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-stone-50 text-[10px] uppercase tracking-widest text-stone-500 font-semibold">
                  <tr>
                    <th className="py-2.5 px-3 text-left">Tanggal</th>
                    <th className="py-2.5 px-3 text-left">Material</th>
                    <th className="py-2.5 px-3 text-left">Lokasi / Klien</th>
                    <th className="py-2.5 px-3 text-left">Actor</th>
                    <th className="py-2.5 px-3 text-left">Perubahan</th>
                    <th className="py-2.5 px-3 text-center">Sumber</th>
                  </tr>
                </thead>
                <tbody>
                  {pageItems.map((r, i) => {
                    const chEntries = Object.entries(r.changes || {});
                    return (
                      <tr key={`${r.alert_id || "log"}-${r.price_id}-${r.ts}-${i}`} className="border-t border-stone-100 align-top" data-testid={`ph-row-${i}`}>
                        <td className="py-3 px-3 whitespace-nowrap">
                          <div className="flex items-center gap-1.5 text-xs text-stone-700">
                            <Calendar className="h-3 w-3 text-stone-400" />
                            {formatDateTime(r.ts)}
                          </div>
                        </td>
                        <td className="py-3 px-3">
                          <div className="flex items-center gap-1.5 font-medium text-stone-900">
                            <Package className="h-3.5 w-3.5 text-stone-400" />
                            {r.material_name || <span className="text-stone-400 italic">— dihapus —</span>}
                          </div>
                          {r.unit && <div className="text-[10px] text-stone-500 mt-0.5">Satuan: {r.unit}</div>}
                        </td>
                        <td className="py-3 px-3 text-xs text-stone-600">
                          {r.location && <div>{r.location}</div>}
                          {r.client_name && <div className="text-[10px] text-stone-500 mt-0.5">{r.client_name}</div>}
                          {!r.location && !r.client_name && "—"}
                        </td>
                        <td className="py-3 px-3">
                          <div className="flex items-center gap-1.5 text-xs text-stone-700">
                            <User className="h-3 w-3 text-stone-400" />
                            {r.actor_name || "—"}
                          </div>
                        </td>
                        <td className="py-3 px-3 min-w-[280px]">
                          {chEntries.length === 0 ? (
                            <span className="text-xs text-stone-400 italic">Tidak ada detail</span>
                          ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
                              {chEntries.map(([field, ch]) => (
                                <ChangeCell key={field} field={field} ch={ch} />
                              ))}
                            </div>
                          )}
                        </td>
                        <td className="py-3 px-3 text-center">
                          <Badge variant={r.source === "alert" ? "success" : "neutral"}>
                            {r.source === "alert" ? "Alert" : "Log"}
                          </Badge>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {Pagination}
          </>
        )}
      </Card>
    </div>
  );
}
